import { privateRequest } from "../axios";
import type { ApiResponse, ListData } from "@/types/common";
export type EventType = "MARQUEE" | "MICRO_MEETUPS" | "MASTERCLASS";

export type PublishingStatus = "DRAFT" | "PUBLISHED" | "SCHEDULED";
// and `rsvpClosesOn`'s capitalized duplicate `RsvpClosesOn` was confirmed
// gone for good (see decision #40), not a live ambiguity.
export interface CreateEventPayload {
  name: string;
  eventType: EventType;
  description: string;
  // ISO-8601 OffsetDateTime, per the user — e.g.
  // "2026-09-18T18:00:00+05:30". Both fields carry the event's start
  // instant (selected date + start time, admin's local offset); see
  // CreateGathering.tsx's `buildEventPayload`.
  eventDate: string;
  eventStartTime: string;
  // Bare 24h "HH:MM" (unlike `eventStartTime`, no date/offset). Not currently sent —
  // CreateGathering's UI has no "End time" field. Was briefly restored
  // (decision #43) then swapped back out for "Guests arrive by" per the
  // user's next instruction; left here since the working example body
  // confirms the backend accepts/expects it.
  eventEndTime?: string;
  capacity: number;
  thumbnailUrl?: string;
  guidelines?: string;
  ticketPrice?: number;
  dressCode?: string;
  hostedBy?: string;
  // Sent as a single comma-separated string (e.g. "CHARTERED,UNCHARTERED"),
  // not a JSON array, per the user — unlike the read-side `Event.
  // visibleToCategories` below, which does come back as a real array.
  visibleToCategories?: string;
  cityId?: number;
  cityIds?: string;
  // ISO-8601 OffsetDateTime string, per the user — e.g.
  // "2026-09-14T18:00:00+05:30" or "2026-09-14T12:30:00Z" (was a plain
  // local datetime with no offset before this was corrected; see
  // CreateGathering.tsx's `toOffsetDateTime`).
  rsvpClosesOn?: string;
  // ISO-8601 OffsetDateTime, per the user — the event's date at the
  // arrival time, e.g. "2026-09-18T17:30:00+05:30". Swapped in for "End
  // time" per the user — CreateGathering's "Guests arrive by" field
  // (restored from decision #34's removal) now feeds this directly.
  guestArrivesBy?: string;
  publishingStatus?: PublishingStatus;
  // Non-optional on this type — CreateGathering.tsx always sets this key
  // (`null` when there's nothing to schedule), matching the confirmed
  // working JSON example body. `buildEventFormData` below omits the key
  // entirely when it's `null` when encoding as multipart, since a
  // multipart field has no native null the way a JSON key does.
  scheduledPublishAt: string | null;
  venue?: string;
  neighbourhood?: string;
  mapUrl?: string;
}

// Confirmed shape from a real `GET /events` response (a 30-record sample
// given directly by the user matches this field-for-field, no changes
// needed) — unlike the POST body above, times here are full ISO datetime
// strings (not a {hour,minute,second,nano} object), and several fields the
// POST body doesn't accept (ticketPrice, dressCode, hostedBy,
// publishingStatus, cities, ...) do come back on read. Only "PUBLISHED"
// and `null` have been observed for `publishingStatus`; other values (e.g.
// a "DRAFT"/"SCHEDULED" string) are not confirmed. `visibleToCategories`
// confirmed both "CHARTERED" and "UNCHARTERED" as real values (see
// CreateGathering.tsx's `ROOM_TO_CATEGORIES`). That sample also has real
// events where `eventEndTime` predates `eventStartTime`/`eventDate` by
// hours to months (e.g. id 79, id 87) — backend data-quality issues, not
// something to "fix" by inventing client-side date logic; `eventEndTime`
// is rendered as a bare time (see `formatEventTime` in
// pages/Gatherings/lib/eventFormat.ts) so a mismatched date doesn't
// visibly break anything, just doesn't reflect the real gap.
// Confirmed real via a `GET /events/{eventId}` sample given directly by the
// user (see `getEventById` below — a distinct endpoint from the `GET
// /events` list above, whose own 30-record sample never showed this
// field). `cities` (above) came back `null` on that same sample while this
// carried the actual city (`cityName`), so every place that read
// `event.cities?.[0]` as "the event's city" was reading a field this
// endpoint doesn't populate; see `getEventCityName` in
// pages/Gatherings/lib/eventFormat.ts and docs/decisions.md. Not confirmed
// whether the list endpoint's own items ever populate `locations` too —
// CreateGathering.tsx's edit flow now sources from `getEventById`
// specifically rather than assuming so. Only a single entry has been
// observed on the wire so far — plural is assumed from the field's own
// name, not independently confirmed for a multi-location event.
export interface EventLocation {
  id: number;
  name: string;
  address: string;
  mapUrl: string | null;
  cityName: string;
}

export interface Event {
  id: number;
  name: string;
  eventType: EventType;
  description: string;
  eventDate: string;
  eventStartTime: string;
  eventEndTime: string;
  capacity: number;
  // Confirmed via a real `GET /events/{eventId}` sample given directly by
  // the user (0 on that sample) — same "only confirmed on the by-id
  // endpoint, not the 30-record list sample" caveat as `locations` above,
  // per decision #93/#94: GatheringCard (fed by the list endpoint) treats
  // a missing value as 0 rather than assuming it's always populated.
  filledCapacity?: number;
  thumbnailUrl: string | null;
  guidelines: string | null;
  mapUrl: string | null;
  visibleToCategories: string[];
  // Confirmed real, but `null` on the one real sample seen so far — see
  // `EventLocation`/`locations` above for the field that actually carries
  // the city on that same sample.
  cities: string[] | null;
  locations: EventLocation[] | null;

  cityIds: number[] | null;
  ticketPrice: number | null;
  dressCode: string | null;
  hostedBy: string | null;
  publishingStatus: string | null;
  scheduledPublishAt: string | null;
  rsvpClosesOn: string | null;
  guestArrivesBy: string | null;
}

export interface GetEventsParams {
  pageNo?: number;
  perPage?: number;
  // Filters to events on or before this date — requested by the user for
  // PastEvents.tsx. Must be a full ISO-8601 datetime string (e.g.
  // "2026-09-04T23:59:59.999Z"), confirmed against the real endpoint: a
  // bare "YYYY-MM-DD" (`eventDate`'s own write-side shape) makes this
  // endpoint 500 ("Something went wrong") every time, not just reject the
  // filter — see docs/decisions.md.
  toDate?: string;
  fromDate?: string;
  eventType?: EventType | undefined;
}

// Monthly aggregates `GET /events` returns alongside its list — fed into
// Gatherings.tsx's "Seats Filled This Month" / "Ticket Revenue (MTD)"
// stat cards. Optional since other callers (e.g. Ticketing.tsx) don't rely
// on them.
export interface EventsListData extends ListData<Event> {
  totalSeatsThisMonth?: number;
  seatsFilledThisMonth?: number;
  ticketRevenueThisMonth?: number;
  ticketRevenueLastMonth?: number;
  hikePercentage?: number;
  lastMonthName?: string;
}

const ENDPOINTS = {
  EVENTS: "/events",
};

const getEvents = async (params?: GetEventsParams) => {
  const response = await privateRequest.get<ApiResponse<EventsListData>>(ENDPOINTS.EVENTS, { params });
  return response.data;
};

export interface EventParticipant {
  participantId: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  preferredLocation: string;
  locationAssigned: string;
  subscriptionName: string;
  paymentPaid: number;
}

export interface GetEventParticipantsParams {
  status?: string;
  isLocationAssigned?: boolean;
  pageNo?: number;
  perPage?: number;
  search?: string | undefined;
}

export interface EventParticipantsData extends ListData<EventParticipant> {
  capacity: number | null;
  filledCapacity: number | null;
  ticketRevenue: number | null;
}

const getEventParticipants = async (eventId: number, params: GetEventParticipantsParams) => {
  const response = await privateRequest.get<ApiResponse<EventParticipantsData>>(
    `${ENDPOINTS.EVENTS}/${eventId}/participants`,
    { params },
  );
  return response.data;
};

// Same endpoint as `getEventParticipants` above, `isExport: true` added per
// its Swagger param list — same `isExport`-returns-a-file convention as
// `services/api/support.ts`'s `exportSupportItems`.
const exportEventParticipants = async (eventId: number, params?: Pick<GetEventParticipantsParams, "search">) => {
  return privateRequest.get(`${ENDPOINTS.EVENTS}/${eventId}/participants`, {
    params: { ...params, isExport: true },
    responseType: "blob",
  });
};

// Real `GET /admin/events/{eventId}`, given directly by the user —
// CreateGathering.tsx's edit flow now uses this instead of finding the
// event inside the `GET /events` list already in Redux, since that list's
// own items aren't confirmed to carry `locations` (see its own comment
// above) the way this per-id response does. The envelope shape (`data`
// wrapping the `Event` directly, not a `ListData`) isn't independently
// confirmed either — assumed from the same convention every other
// non-list endpoint here uses; fix this, not callers, if a real response
// nests it differently.
const getEventById = async (eventId: number) => {
  const response = await privateRequest.get<ApiResponse<Event>>(`${ENDPOINTS.EVENTS}/${eventId}`);
  return response.data;
};

// `createEvent`/`updateEvent` used to be a plain JSON POST/PUT, with the
// cover image sent separately afterward via its own
// `POST /events/{id}/thumbnail` call (a real, confirmed endpoint). Per the
// user, that's now one request: the same payload fields plus the image
// under a `file` field, multipart/form-data instead of JSON — no sample
// multipart request was given for this combined shape (unlike the
// thumbnail-only endpoint's own real confirmed sample), so this encoding
// is a guess, not a verified contract. Each payload field is appended to
// the `FormData` individually under its own key (matching a
// `@ModelAttribute`/`@RequestParam`-per-field Spring binding, the most
// common shape for a combined fields+file endpoint) — a `null`/`undefined`
// field is omitted entirely rather than sent as the literal string
// `"null"` (multipart has no native null; an absent key is the closest
// analogue to JSON `null` for an optional field), and an array/object value
// (`chapterIds`; `visibleToCategories` is sent as a plain comma-separated
// string, not an array, per the user) is `JSON.stringify`'d since
// `FormData` can only hold strings/Blobs. If a
// real submission comes back with a validation error on a specific field,
// that field's encoding here — not the overall approach — is the first
// thing to check.
const buildEventFormData = (payload: CreateEventPayload | UpdateEventPayload, file?: File) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    formData.append(key, typeof value === "object" ? JSON.stringify(value) : String(value));
  });
  if (file) formData.append("file", file);
  return formData;
};

// `data`'s exact shape for a create response isn't independently
// confirmed — assumed to wrap the created `Event` following the same
// envelope every other confirmed endpoint uses. Fix this, not callers, if
// a real response nests it differently.
const createEvent = async (payload: CreateEventPayload, file?: File) => {
  const response = await privateRequest.post<ApiResponse<Event>>(ENDPOINTS.EVENTS, buildEventFormData(payload, file));
  return response.data;
};

// Editing uses `PUT /admin/events/{eventId}` with eventId in the path.
export interface UpdateEventPayload extends CreateEventPayload {
  eventId?: number;
  gatheringId?: number;
}

const updateEvent = async (payload: UpdateEventPayload, file?: File) => {
  const id = payload.eventId ?? payload.gatheringId;
  if (!id) {
    throw new Error("eventId is required for updating an event");
  }
  const response = await privateRequest.put<ApiResponse<Event>>(`${ENDPOINTS.EVENTS}/${id}`, buildEventFormData(payload, file));
  return response.data;
};

// Real endpoint given directly by the user: POST /admin/events/attendance/mark
// with `{ code }` — the code read from a member's ticket QR (or typed in
// manually) on the Door Check-In screen. Response shape not confirmed yet,
// so only the envelope's `message` is used.
const markAttendance = async (code: string) => {
  const response = await privateRequest.post<ApiResponse<unknown>>(`${ENDPOINTS.EVENTS}/attendance/mark`, { code });
  return response.data;
};

const eventService = {
  getEvents,
  markAttendance,
  getEventById,
  getEventParticipants,
  exportEventParticipants,
  createEvent,
  updateEvent,
};

export default eventService;
